import React from 'react';
import { Pole, Cable } from '../../types';
import { getFiberColorInBuffer, FIBER_COLORS } from '../../utils/fiberColors';
import { indexPortovSplitterov, portNaVlakne } from '../../utils/zakaznickeVlakna';
import './CassetteDocumentation.css';

interface CassetteDocumentationProps {
  pole: Pole;
  cables: Cable[];
  /** Všetky stĺpy — treba na nájdenie zákazníckej kazety (virtuálne dvojča) */
  poles?: Pole[];
}

// Farby vlákien mal tento súbor vlastné a po anglicky („Blue", „Orange"…),
// hoci spoločná slovenská tabuľka existuje. Zjednotené 2026-09-30.

export const CassetteDocumentation: React.FC<CassetteDocumentationProps> = ({ pole, cables, poles = [] }) => {
  const getCableName = (cableId: number): string => {
    const cable = cables.find((c) => c.id === cableId);
    if (!cable) return `Kábel #${cableId}`;
    // Zákaznícke káble majú nulovú dĺžku — „(0.00 m)" je len šum.
    const d = Number(cable.length_meters) || 0;
    return d > 0 ? `Kábel #${cableId} (${d.toFixed(2)} m)` : `Kábel #${cableId}`;
  };

  const getFiberColor = (fiberNumber: number): string => getFiberColorInBuffer(fiberNumber, 24);

  // Zákaznícke kazety sedia na virtuálnom dvojčati stĺpa — bez neho by tu
  // zákazníci zapísaní technikom vôbec neboli. Doplnené 2026-09-30.
  const dvojcata = poles.filter((p) => p.parent_pole_id === pole.id && p.is_virtual);

  // Prípojky z hlavnej aj zo zákazníckej kazety — je to jeden fyzický stĺpik.
  const portyNaVlaknach = indexPortovSplitterov(poles);
  const pripojky = [pole, ...dvojcata].flatMap((zdroj) =>
    (((zdroj.cassette as any)?.drops || []) as any[]).map((d: any) => ({
      ...d,
      kde: zdroj,
      port: portNaVlakne(portyNaVlaknach, d.cable_id, d.fiber),
    })),
  );

  // Zoskupené po splitteroch — plochá tabuľka opakovala v každom riadku ten
  // istý splitter aj vstupný kábel a bola neprehľadná. Teraz je to blok na
  // splitter a v ňom výstupy ako na montážnej obrazovke.
  const zakaznickeBloky = [pole, ...dvojcata]
    .flatMap((zdroj) =>
      ((zdroj.cassette?.splitters || []) as any[]).map((sp: any, si: number) => {
        const vystupy = ((sp.outputs || []) as any[]).map((o: any, oi: number) => ({
          cislo: oi + 1,
          farba: getFiberColor(oi + 1),
          hex: FIBER_COLORS[oi % FIBER_COLORS.length].color,
          zakaznik: o?.customer_name
            ? {
                meno: String(o.customer_name).trim(),
                telefon: o.customer_phone || null,
                adresa: o.customer_address || null,
                osadil: o.installed_by || null,
                kedy: o.installed_at || null,
              }
            : null,
        }));
        return {
          stlp: zdroj, splitter: sp, index: si, vystupy,
          obsadenych: vystupy.filter((v) => v.zakaznik).length,
        };
      })
    )
    .filter((b) => b.obsadenych > 0);

  const pocetZakaznikov = zakaznickeBloky.reduce((n, b) => n + b.obsadenych, 0);

  if (!pole.cassette?.has_cassette && pocetZakaznikov === 0) {
    return (
      <div className="cassette-doc">
        <div className="doc-header">
          <h2>📦 Dokumentácia kaziet</h2>
          <p className="no-cassette">Na tomto stĺpiku nie je nastavená kazeta</p>
        </div>
      </div>
    );
  }

  // Stĺp nemusí mať vlastnú kazetu — zákazníci môžu byť len na jeho
  // virtuálnom dvojčati, a aj vtedy chceme sekciu zobraziť.
  const cassette = pole.cassette || ({} as NonNullable<typeof pole.cassette>);

  return (
    <div className="cassette-doc">
      <div className="doc-header">
        <h2>📦 Dokumentácia kaziet</h2>
        <h3>{pole.name}</h3>
        <p className="doc-subtitle">Technický montážny podklad</p>
      </div>

      <div className="doc-content">
        {/* Pripojení zákazníci — zoskupené po splitteroch. Plochá tabuľka
            opakovala v každom riadku ten istý splitter aj vstupný kábel. */}
        {pocetZakaznikov > 0 && (
          <div className="doc-section">
            <h3>👤 Pripojení zákazníci ({pocetZakaznikov})</h3>
            <p className="section-description">
              Zapísané technikom pri montáži. Číslo výstupu určuje farbu vlákna
              rovnako ako na montážnej obrazovke.
            </p>

            {zakaznickeBloky.map((b, bi) => (
              <div
                key={bi}
                style={{
                  border: '1px solid #d8dde3', borderRadius: '8px',
                  marginBottom: '14px', overflow: 'hidden', pageBreakInside: 'avoid',
                }}
              >
                {/* hlavička bloku — splitter a jeho vstup, raz, nie v každom riadku */}
                <div style={{
                  background: '#eef2f7', padding: '8px 12px',
                  display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '10px',
                }}>
                  <strong>Splitter {b.splitter.type}</strong>
                  <span style={{ color: '#555', fontSize: '13px' }}>
                    vstup:{' '}
                    {b.splitter.input_source_type === 'olt_port'
                      ? `OLT Port ${b.splitter.input_olt_port}`
                      : b.splitter.input_cable_id
                      ? `${getCableName(b.splitter.input_cable_id)}, vlákno č.${b.splitter.input_fiber || '?'} (${getFiberColor(b.splitter.input_fiber || 1)})`
                      : 'neurčený'}
                  </span>
                  {b.stlp.id !== pole.id && (
                    <span style={{ color: '#777', fontSize: '12px' }}>
                      · zákaznícka kazeta {b.stlp.name}
                    </span>
                  )}
                  <span style={{
                    marginLeft: 'auto', fontSize: '13px', fontWeight: 600,
                    color: b.obsadenych === b.vystupy.length ? '#b45309' : '#166534',
                  }}>
                    {b.obsadenych}/{b.vystupy.length} obsadených
                  </span>
                </div>

                {/* výstupy */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '10px' }}>
                  {b.vystupy.map((v) => (
                    <div
                      key={v.cislo}
                      style={{
                        flex: '1 1 190px', minWidth: 0,
                        border: '1px solid ' + (v.zakaznik ? '#c9d4e0' : '#e6e6e6'),
                        borderRadius: '6px', padding: '8px 10px',
                        background: v.zakaznik ? '#fff' : '#fafafa',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <span style={{
                          display: 'inline-block', width: '12px', height: '12px',
                          borderRadius: '3px', background: v.hex,
                          border: '1px solid rgba(0,0,0,0.25)', flexShrink: 0,
                        }} />
                        <strong style={{ fontSize: '13px' }}>#{v.cislo}</strong>
                        <span style={{ fontSize: '12px', color: '#666' }}>{v.farba}</span>
                      </div>

                      {v.zakaznik ? (
                        <>
                          <div style={{ fontWeight: 600, fontSize: '13px' }}>{v.zakaznik.meno}</div>
                          {(v.zakaznik.telefon || v.zakaznik.adresa) && (
                            <div style={{ fontSize: '12px', color: '#555' }}>
                              {[v.zakaznik.telefon, v.zakaznik.adresa].filter(Boolean).join(' · ')}
                            </div>
                          )}
                          {(v.zakaznik.osadil || v.zakaznik.kedy) && (
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>
                              {v.zakaznik.osadil}
                              {v.zakaznik.kedy &&
                                ` · ${new Date(v.zakaznik.kedy).toLocaleDateString('sk-SK')}`}
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ fontSize: '13px', color: '#999', fontStyle: 'italic' }}>Voľný</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pass-Through Cables */}
        {cassette.pass_through_cables && cassette.pass_through_cables.length > 0 && (
          <div className="doc-section">
            <h3>🔄 Priechodné káble</h3>
            <p className="section-description">
              Káble prechádzajúce cez stĺp. Vlákna pokračujú bez prerušenia, okrem odtapovaných.
            </p>

            {cassette.pass_through_cables.map((pt, idx) => (
              <div key={idx} className="doc-item pass-through">
                <div className="item-header">
                  <h4>Priechod č.{idx + 1}</h4>
                </div>

                <div className="item-details">
                  <div className="detail-row">
                    <span className="label">FROM Cable (vstupný):</span>
                    <span className="value">{getCableName(pt.from_cable_id)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">TO Cable (výstupný):</span>
                    <span className="value">{getCableName(pt.to_cable_id)}</span>
                  </div>
                </div>

                <div className="fiber-table">
                  <h5>Stav vlákna:</h5>
                  <table>
                    <thead>
                      <tr>
                        <th>Vlákno č.</th>
                        <th>Farba</th>
                        <th>Stav</th>
                        <th>Cieľ odpichu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pt.fibers.map((fiber) => (
                        <tr key={fiber.fiber_number} className={fiber.is_tapped ? 'tapped' : ''}>
                          <td>#{fiber.fiber_number}</td>
                          <td>
                            <span className={`fiber-color-badge ${getFiberColor(fiber.fiber_number).toLowerCase()}`}>
                              {getFiberColor(fiber.fiber_number)}
                            </span>
                          </td>
                          <td>
                            {fiber.is_tapped ? (
                              <span className="status-badge tapped">✓ ODPICHNUTÉ</span>
                            ) : (
                              <span className="status-badge pass">→ PRIECHOD</span>
                            )}
                          </td>
                          <td>
                            {fiber.is_tapped && fiber.tap_to_cable_id && fiber.tap_to_fiber ? (
                              <span>
                                {getCableName(fiber.tap_to_cable_id)} Fiber #{fiber.tap_to_fiber} ({getFiberColor(fiber.tap_to_fiber)})
                                {fiber.tap_loss_db && <span className="loss"> • Útlm: {fiber.tap_loss_db} dB</span>}
                              </span>
                            ) : (
                              <span className="na">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Prípojky k zákazníkom — vlákno, ktoré odtiaľto ide k domu. Splitter,
            z ktorého prišlo, môže byť na inom stĺpiku, preto je tu napísané kde. */}
        {pripojky.length > 0 && (
          <div className="doc-section">
            <h3>🏠 Prípojky k zákazníkom</h3>
            <p className="section-description">
              Koniec vlákna idúceho k domu zákazníka. Zákaznícky splitter, ktorý
              ho kŕmi, môže stáť na inom stĺpiku.
            </p>

            <table className="splice-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Kábel</th>
                  <th>Vlákno</th>
                  <th>Kazeta</th>
                  <th>Port splittra</th>
                  <th>Zákazník</th>
                </tr>
              </thead>
              <tbody>
                {pripojky.map((pr: any, idx: number) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{getCableName(pr.cable_id)}</td>
                    <td>{pr.fiber} ({getFiberColor(pr.fiber)})</td>
                    <td>{pr.kde.id === pole.id ? 'hlavná' : `zákaznícka · ${pr.kde.name}`}</td>
                    <td>
                      {pr.port
                        ? `#${pr.port.vystup} splittra ${pr.port.splitterTyp || ''} · ${pr.port.poleName}`
                        : '— na vlákne nie je zákaznícky splitter'}
                    </td>
                    <td>
                      {pr.port
                        ? pr.port.zakaznik || 'VOĽNÝ'
                        : '-'}
                      {pr.label ? ` · ${pr.label}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Splices */}
        {cassette.splices && cassette.splices.length > 0 && (
          <div className="doc-section">
            <h3>🔗 Zvary</h3>
            <p className="section-description">
              Spojenie dvoch vlákien zvarom. Každý zvar má stratu signálu.
            </p>

            <table className="splice-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Z kábla</th>
                  <th>Z vlákna</th>
                  <th></th>
                  <th>Do kábla</th>
                  <th>Do vlákna</th>
                  <th>Útlm</th>
                </tr>
              </thead>
              <tbody>
                {cassette.splices.map((splice, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>
                      {splice.from_source_type === 'olt_port'
                        ? `OLT Port ${splice.from_olt_port}`
                        : getCableName(splice.from_cable_id!)}
                    </td>
                    <td>
                      {splice.from_source_type === 'olt_port' ? (
                        <span className="fiber-badge">-</span>
                      ) : (
                        <span className="fiber-badge">
                          #{splice.from_fiber} ({getFiberColor(splice.from_fiber!)})
                        </span>
                      )}
                    </td>
                    <td className="arrow">→</td>
                    <td>{getCableName(splice.to_cable_id)}</td>
                    <td>
                      <span className="fiber-badge">
                        #{splice.to_fiber} ({getFiberColor(splice.to_fiber)})
                      </span>
                    </td>
                    <td>
                      <span className="loss-badge">{splice.loss_db || 0.1} dB</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Splitters */}
        {cassette.splitters && cassette.splitters.length > 0 && (
          <div className="doc-section">
            <h3>📡 Splittery</h3>
            <p className="section-description">
              Pasívne rozdeľovače signálu. Jeden vstup → viacero výstupov.
            </p>

            {cassette.splitters.map((splitter, idx) => (
              <div key={idx} className="doc-item splitter">
                <div className="item-header">
                  <h4>Splitter č.{idx + 1}</h4>
                  <span className="splitter-type">{splitter.type}</span>
                </div>

                <div className="item-details">
                  <div className="detail-row">
                    <span className="label">Vstupný zdroj:</span>
                    <span className="value">
                      {splitter.input_source_type === 'olt_port'
                        ? `OLT Port ${splitter.input_olt_port}`
                        : `${getCableName(splitter.input_cable_id!)} - vlákno č.${splitter.input_fiber} (${getFiberColor(splitter.input_fiber!)})`}
                    </span>
                  </div>
                </div>

                <div className="splitter-outputs">
                  <h5>Výstupy ({splitter.outputs.length}):</h5>
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Výstupný kábel</th>
                        <th>Výstupné vlákno</th>
                      </tr>
                    </thead>
                    <tbody>
                      {splitter.outputs.map((output, outIdx) => (
                        <tr key={outIdx}>
                          <td>{outIdx + 1}</td>
                          <td>{output.cable_id ? getCableName(output.cable_id) : 'Nepripojené'}</td>
                          <td>
                            {output.fiber ? (
                              <span className="fiber-badge">
                                #{output.fiber} ({getFiberColor(output.fiber)})
                              </span>
                            ) : (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>Nepripojené</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        <div className="doc-section summary">
          <h3>📊 Zhrnutie</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-label">Priechodné káble:</span>
              <span className="summary-value">{cassette.pass_through_cables?.length || 0}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Zvary:</span>
              <span className="summary-value">{cassette.splices?.length || 0}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Splittery:</span>
              <span className="summary-value">{cassette.splitters?.length || 0}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Odpichnutých vlákien spolu:</span>
              <span className="summary-value">
                {cassette.pass_through_cables?.reduce(
                  (sum, pt) => sum + pt.fibers.filter((f) => f.is_tapped).length,
                  0
                ) || 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="doc-footer">
        <p>Vytvorené: {new Date().toLocaleString()}</p>
        <p>Projekt: {pole.project_id}</p>
      </div>
    </div>
  );
};

